import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation files
import commonDE from './locales/de/common.json';
import commonEN from './locales/en/common.json';
import dashboardDE from './locales/de/dashboard.json';
import dashboardEN from './locales/en/dashboard.json';
import navigationDE from './locales/de/navigation.json';
import navigationEN from './locales/en/navigation.json';
import leadsDE from './locales/de/leads.json';
import leadsEN from './locales/en/leads.json';
import tasksDE from './locales/de/tasks.json';
import tasksEN from './locales/en/tasks.json';
import opportunitiesDE from './locales/de/opportunities.json';
import opportunitiesEN from './locales/en/opportunities.json';
import settingsDE from './locales/de/settings.json';
import settingsEN from './locales/en/settings.json';
import offersDE from './locales/de/offers.json';
import offersEN from './locales/en/offers.json';
import streamDE from './locales/de/stream.json';
import streamEN from './locales/en/stream.json';
import companiesDE from './locales/de/companies.json';
import companiesEN from './locales/en/companies.json';
import insightsDE from './locales/de/insights.json';
import insightsEN from './locales/en/insights.json';
import googleAdsDE from './locales/de/googleAds.json';
import googleAdsEN from './locales/en/googleAds.json';
import materialDE from './locales/de/material.json';
import materialEN from './locales/en/material.json';
import authDE from './locales/de/auth.json';
import authEN from './locales/en/auth.json';
import automateDE from './locales/de/automate.json';
import automateEN from './locales/en/automate.json';
import closingDE from './locales/de/closing.json';
import closingEN from './locales/en/closing.json';
import aiDE from './locales/de/ai.json';
import aiEN from './locales/en/ai.json';
import onboardingDE from './locales/de/onboarding.json';
import onboardingEN from './locales/en/onboarding.json';
import quickSelectDE from './locales/de/quickSelect.json';
import quickSelectEN from './locales/en/quickSelect.json';
import aiSuggestionsDE from './locales/de/aiSuggestions.json';
import aiSuggestionsEN from './locales/en/aiSuggestions.json';
import meetingPrepDE from './locales/de/meetingPrep.json';
import meetingPrepEN from './locales/en/meetingPrep.json';
import expensesDE from './locales/de/expenses.json';
import expensesEN from './locales/en/expenses.json';
import softwareComplianceDE from './locales/de/softwareCompliance.json';
import softwareComplianceEN from './locales/en/softwareCompliance.json';
import guideDE from './locales/de/guide.json';
import guideEN from './locales/en/guide.json';
import customersDE from './locales/de/customers.json';
import customersEN from './locales/en/customers.json';
import customerIntelligenceDE from './locales/de/customerIntelligence.json';
import customerIntelligenceEN from './locales/en/customerIntelligence.json';
import timeTrackingDE from './locales/de/timeTracking.json';
import timeTrackingEN from './locales/en/timeTracking.json';
import teamSkillsDE from './locales/de/teamSkills.json';
import teamSkillsEN from './locales/en/teamSkills.json';
import developmentDE from './locales/de/development.json';
import developmentEN from './locales/en/development.json';
import productsDE from './locales/de/products.json';
import productsEN from './locales/en/products.json';
import businessPlanDE from './locales/de/businessPlan.json';
import businessPlanEN from './locales/en/businessPlan.json';
import engagementsDE from './locales/de/engagements.json';
import engagementsEN from './locales/en/engagements.json';
import timelineDE from './locales/de/timeline.json';
import timelineEN from './locales/en/timeline.json';

export const defaultNS = 'common';
export const supportedLanguages = ['de', 'en'] as const;
export type SupportedLanguage = typeof supportedLanguages[number];

export const resources = {
  de: {
    common: commonDE,
    dashboard: dashboardDE,
    navigation: navigationDE,
    leads: leadsDE,
    tasks: tasksDE,
    opportunities: opportunitiesDE,
    settings: settingsDE,
    offers: offersDE,
    stream: streamDE,
    companies: companiesDE,
    insights: insightsDE,
    googleAds: googleAdsDE,
    material: materialDE,
    auth: authDE,
    automate: automateDE,
    closing: closingDE,
    ai: aiDE,
    onboarding: onboardingDE,
    quickSelect: quickSelectDE,
    aiSuggestions: aiSuggestionsDE,
    meetingPrep: meetingPrepDE,
    expenses: expensesDE,
    softwareCompliance: softwareComplianceDE,
    guide: guideDE,
    customers: customersDE,
    customerIntelligence: customerIntelligenceDE,
    timeTracking: timeTrackingDE,
    teamSkills: teamSkillsDE,
    development: developmentDE,
    products: productsDE,
    businessPlan: businessPlanDE,
    engagements: engagementsDE,
    timeline: timelineDE,
  },
  en: {
    common: commonEN,
    dashboard: dashboardEN,
    navigation: navigationEN,
    leads: leadsEN,
    tasks: tasksEN,
    opportunities: opportunitiesEN,
    settings: settingsEN,
    offers: offersEN,
    stream: streamEN,
    companies: companiesEN,
    insights: insightsEN,
    googleAds: googleAdsEN,
    material: materialEN,
    auth: authEN,
    automate: automateEN,
    closing: closingEN,
    ai: aiEN,
    onboarding: onboardingEN,
    quickSelect: quickSelectEN,
    aiSuggestions: aiSuggestionsEN,
    meetingPrep: meetingPrepEN,
    expenses: expensesEN,
    softwareCompliance: softwareComplianceEN,
    guide: guideEN,
    customers: customersEN,
    customerIntelligence: customerIntelligenceEN,
    timeTracking: timeTrackingEN,
    teamSkills: teamSkillsEN,
    development: developmentEN,
    products: productsEN,
    businessPlan: businessPlanEN,
    engagements: engagementsEN,
    timeline: timelineEN,
  },
} as const;

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    defaultNS,
    fallbackLng: 'de',
    supportedLngs: supportedLanguages,
    
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },

    interpolation: {
      escapeValue: false, // React already escapes
    },

    react: {
      useSuspense: false, // Disable suspense for better SSR compatibility
    },
  });

export default i18n;

// Helper function to change language
export const changeLanguage = (lng: SupportedLanguage) => {
  i18n.changeLanguage(lng);
  localStorage.setItem('i18nextLng', lng);
};

// Get current language
export const getCurrentLanguage = (): SupportedLanguage => {
  const lng = i18n.language;
  return supportedLanguages.includes(lng as SupportedLanguage) 
    ? lng as SupportedLanguage 
    : 'de';
};
